import Phaser from 'phaser';
import { GameState } from './game/GameState';
import { UIManager } from './ui/UIManager';
import { MainScene } from './scenes/MainScene';
import { TouchHandler, addHapticToButtons } from './utils/TouchHandler';
import { ThemeManager } from './utils/ThemeManager';
import { tutorialManager } from './utils/TutorialManager';
import { audioManager } from './utils/AudioManager';
import { purchaseProject } from './data/projects';
import { GAME_LOOP, THRESHOLDS, QUANTUM, CREATIVITY } from './game/constants';

// Initialize game state (faithful to original variable names)
export const gameState = new GameState();

// Initialize UI manager
export const uiManager = new UIManager(gameState);

// Phaser configuration for particle effects
// OPTIMIZED: Removed unused physics engine (saves ~50KB bundle size + initialization overhead)
const config: Phaser.Types.Core.GameConfig = {
  type: Phaser.AUTO,
  parent: 'phaser-container',
  transparent: true,
  width: window.innerWidth,
  height: window.innerHeight,
  scene: [MainScene],
  scale: {
    mode: Phaser.Scale.RESIZE,
    autoCenter: Phaser.Scale.CENTER_BOTH,
  },
  render: {
    pixelArt: false,
    antialias: true,
    desynchronized: true, // Better mobile performance
    powerPreference: 'high-performance',
  },
};

// Create Phaser game instance
export const phaserGame = new Phaser.Game(config);

// Get reference to MainScene for visual effects
let mainScene: MainScene | null = null;
phaserGame.events.on('ready', () => {
  mainScene = phaserGame.scene.getScene('MainScene') as MainScene;
  // Removed window exposure for security
});

// Expose audioManager to window for UI callbacks
window.audioManager = audioManager;

// Initialize the UI
uiManager.init();

// Start tutorial for first-time users
setTimeout(() => {
  if (tutorialManager.shouldShowTutorial()) {
    tutorialManager.start();
  }
}, GAME_LOOP.TUTORIAL_DELAY_MS);

// Initialize theme manager
new ThemeManager();

// Initialize touch handler for advanced mobile features
const touchHandler = new TouchHandler(document.body, {
  onSwipeLeft: () => {
    // Swipe left to collapse panels
    const panels = document.querySelectorAll('.panel');
    panels.forEach((panel) => {
      const header = panel.querySelector('.panel-header') as HTMLElement;
      if (header && !panel.classList.contains('collapsed')) {
        header.click();
      }
    });
  },
  onSwipeRight: () => {
    // Swipe right to expand panels
    const panels = document.querySelectorAll('.panel');
    panels.forEach((panel) => {
      const header = panel.querySelector('.panel-header') as HTMLElement;
      if (header && panel.classList.contains('collapsed')) {
        header.click();
      }
    });
  },
  onLongPress: () => {
    // Long press for haptic feedback
    touchHandler.triggerHaptic('medium');
  },
});

// Add haptic feedback to all buttons
addHapticToButtons();

// Track state changes for visual effects
let lastMilestone = gameState.milestone;
let lastTrust = gameState.trust;
let lastSpaceFlag = gameState.spaceFlag;
let lastProbeCount = gameState.probeCount;
let lastDrifterCount = gameState.drifterCount;
let clipBurstAccumulator = 0;
let lastEndgameActive = gameState.endgameActive;
let lastDismantlePhase = gameState.dismantlePhase;
let lastProject35 = gameState.projectsFlags[35]; // HypnoDrones
let lastQOps = 0;
let lastCreativity = gameState.creativity;

// Game loops - matching original timing exactly
// Fast loop: 10ms (100Hz) - production, ops, UI updates
let lastFastTick = performance.now();

// Slow loop: 100ms (10Hz) - sales, wire price, revenue
let lastSlowTick = performance.now();

// Auto-save: every 2.5 seconds (25 slow ticks)
let slowTickCount = 0;

function gameLoop() {
  const now = performance.now();

  // Fast loop (10ms)
  if (now - lastFastTick >= GAME_LOOP.FAST_INTERVAL_MS) {
    const delta = now - lastFastTick;
    lastFastTick = now;

    gameState.fastTick(delta);
    uiManager.updateFast();
  }

  // Slow loop (100ms)
  if (now - lastSlowTick >= GAME_LOOP.SLOW_INTERVAL_MS) {
    const delta = now - lastSlowTick;
    lastSlowTick = now;

    gameState.slowTick(delta);
    uiManager.updateSlow();

    // Visual effects - check for state changes
    if (mainScene) {
      // Milestone celebration
      if (gameState.milestone !== lastMilestone) {
        mainScene.showMilestoneEffect();
        lastMilestone = gameState.milestone;
      }

      // Trust increase celebration
      if (gameState.trust !== lastTrust) {
        mainScene.showMilestoneEffect();
        lastTrust = gameState.trust;
      }

      // Space phase transition
      if (gameState.spaceFlag !== lastSpaceFlag) {
        if (gameState.spaceFlag === 1) {
          mainScene.enableSpacePhase();
        } else {
          mainScene.disableSpacePhase();
        }
        lastSpaceFlag = gameState.spaceFlag;
      }

      // Clip production bursts (trigger when accumulated clips is high)
      clipBurstAccumulator += gameState.clipRate * (delta / 1000);
      if (clipBurstAccumulator >= THRESHOLDS.CLIP_BURST_THRESHOLD) {
        const burstX = Math.random() * window.innerWidth * 0.8 + window.innerWidth * 0.1;
        const burstY = Math.random() * window.innerHeight * 0.3 + 100;
        mainScene.showClipBurst(burstX, burstY, clipBurstAccumulator);
        clipBurstAccumulator = 0;
      }

      // Probe launch effects (when probe count increases significantly)
      if (gameState.spaceFlag === 1 && gameState.probeCount > lastProbeCount + THRESHOLDS.PROBE_LAUNCH_DELTA) {
        const launchX = Math.random() * window.innerWidth * 0.6 + window.innerWidth * 0.2;
        const launchY = window.innerHeight * 0.7;
        mainScene.showProbeLaunch(launchX, launchY);
        lastProbeCount = gameState.probeCount;
      }

       // Combat effects (when drifters are being destroyed)
       if (gameState.battleFlag === 1 && gameState.drifterCount < lastDrifterCount - THRESHOLDS.DRIFTER_COMBAT_DELTA) {
         const combatX = Math.random() * window.innerWidth * 0.6 + window.innerWidth * 0.2;
         const combatY = Math.random() * window.innerHeight * 0.4 + window.innerHeight * 0.3;
         mainScene.showCombatEffect(combatX, combatY);
         lastDrifterCount = gameState.drifterCount;
       }
       
       // Update drifter count tracking
       if (Math.abs(gameState.drifterCount - lastDrifterCount) < THRESHOLDS.DRIFTER_COMBAT_DELTA) {
         lastDrifterCount = gameState.drifterCount;
       }

       // PSYCHEDELIC: Endgame reality unraveling effect
       if (gameState.endgameActive && !lastEndgameActive) {
         mainScene.showRealityUnravelingEffect();
         lastEndgameActive = gameState.endgameActive;
       }

       // PSYCHEDELIC: Dismantle phase surreal animation
       if (gameState.dismantlePhase && !lastDismantlePhase) {
         const centerX = window.innerWidth / 2;
         const centerY = window.innerHeight / 2;
         mainScene.showSurrealAnimation(centerX, centerY);
         lastDismantlePhase = gameState.dismantlePhase;
       }

       // PSYCHEDELIC: Major milestone psychedelic effect (every 10 milestones)
       if (gameState.milestone !== lastMilestone && gameState.milestone % THRESHOLDS.MILESTONE_INTERVAL === 0) {
         mainScene.showPsychedelicMilestone();
       }

       // PSYCHEDELIC: HypnoDrones transition (Stage 1 -> 2)
       // This is the "Release the HypnoDrones" moment
       if (gameState.projectsFlags[35] && !lastProject35) {
         mainScene.showPsychedelicMilestone();
         mainScene.showRealityUnravelingEffect(); // It's a major transition
         mainScene.showGlitchEffect(2.0); // Heavy glitch
         lastProject35 = true;
       }

       // PSYCHEDELIC: Quantum glitches
       // Trigger glitch when quantum operations spike (computing a chip)
       if (gameState.qFlag && gameState.qOps > lastQOps + QUANTUM.OPS_SPIKE_THRESHOLD) {
          mainScene.showGlitchEffect(0.3 + (Math.random() * 0.5));
          mainScene.showColorShiftEffect(200);
       }
       lastQOps = gameState.qOps;

       // PSYCHEDELIC: Creativity bursts
       // Trigger color shift when creativity spikes significantly
       if (gameState.creativity > lastCreativity + CREATIVITY.SPIKE_THRESHOLD) {
         mainScene.showColorShiftEffect(500);
       }
       lastCreativity = gameState.creativity;
    }

    // Auto-save
    slowTickCount++;
    if (slowTickCount >= GAME_LOOP.AUTOSAVE_SLOW_TICKS) {
      slowTickCount = 0;
      gameState.autoSave();
    }
  }

  requestAnimationFrame(gameLoop);
}

// Start the game loop
requestAnimationFrame(gameLoop);

// Handle window resize
window.addEventListener('resize', () => {
  uiManager.handleResize();
});

// Expose test API for Playwright tests
// Includes gameState and purchaseProject for accelerated testing
window.__game = {
  get state() {
    return {
      clips: gameState.clips,
      funds: gameState.funds,
      stage: gameState.spaceFlag === 1 ? 3 : (gameState.factoryFlag === 1 ? 2 : 1),
      gameComplete: gameState.gameComplete,
      spaceFlag: gameState.spaceFlag,
    };
  },
  advanceTicks(count: number, updateUi: boolean = false) {
    for (let i = 0; i < count; i++) {
      gameState.fastTick(10);
      if (i % 10 === 0) {
        gameState.slowTick(100);
        if (updateUi) {
          uiManager.updateSlow();
        }
      }
    }
    uiManager.updateFast();
    uiManager.updateSlow();
  },
  reset() {
    // Clear all paperclips-related localStorage (both hyphen and underscore patterns)
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.startsWith('paperclips-') || key.startsWith('paperclips_'))) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
    
    // Also explicitly clear known keys
    localStorage.removeItem('paperclips-save');
    localStorage.removeItem('paperclips_save_0');
    localStorage.removeItem('paperclips_save_1');
    localStorage.removeItem('paperclips_save_2');
    localStorage.removeItem('paperclips-tutorial-completed');
    
    window.location.reload();
  },
};

// Expose gameState and purchaseProject for testing acceleration
window.gameState = gameState;
window.purchaseProject = purchaseProject;
